import { useCan } from '../../core/authorization/PermissionProvider';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import IPagination from '../../main/interfaces/IPagination';
import IGridItems from '../../main/interfaces/IGridItems';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import EditModal from '../../main/components/modal/edit/EditModal';
import eFormType from '../../main/assets/enums/eFormType';
import GeneralTable from '../../main/components/grid/GeneralTable';
import useGetEnums from '../../main/hooks/useGetEnums';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import './NotificationAdmin.css';

type NotificationCategory = {
  /* A Platform GUID for a stored row; `0` is the legacy "new row" sentinel. */
  id: number | string;
  type: number;
  name: string;
  defaultSeverity?: string | null;
  isSystem: boolean;
  isActive: boolean;
  createdAt?: string;
};

type NotificationCategoryGridRow = NotificationCategory & {
  typeId?: number;
};

/*
 * Whether the form edits a stored row. `form.id > 0` was always false for a GUID, so editing a
 * category opened the dialog as "New Category".
 */
const hasId = (id: number | string) => `${id}` !== '' && `${id}` !== '0';

const emptyCategory: NotificationCategory = {
  id: 0,
  type: 1,
  name: '',
  defaultSeverity: 'Info',
  isSystem: false,
  isActive: true,
};

const defaultCategoryGridItems: IGridItems[] = [
  { entity: 'Id', sort: null, filter: null, isShown: true, label: 'Id' },
  { entity: 'Type', sort: null, filter: null, isShown: true, label: 'Type' },
  { entity: 'Name', sort: null, filter: null, isShown: true, label: 'Name' },
  { entity: 'DefaultSeverity', sort: null, filter: null, isShown: true, label: 'DefaultSeverity' },
  { entity: 'IsSystem', sort: null, filter: null, isShown: true, label: 'IsSystem' },
  { entity: 'IsActive', sort: null, filter: null, isShown: true, label: 'IsActive' },
  { entity: 'CreatedAt', sort: 'desc', filter: null, isShown: true, label: 'CreatedAt' },
];

const normalizeCategoryGridItems = (items?: IGridItems[]) => {
  const validEntities = new Set(defaultCategoryGridItems.map((x) => x.entity.toLowerCase()));
  const nextItems = (items ?? []).filter((x) => validEntities.has(String(x.entity ?? '').toLowerCase()));
  return nextItems.length ? nextItems : defaultCategoryGridItems;
};

const NotificationCategoriesGrid = () => {
  const canCreate = useCan("platform.notification-configuration", "create");
  const canUpdate = useCan("platform.notification-configuration", "update");
  const canDelete = useCan("platform.notification-configuration", "delete");
  const { i18n } = useTranslation();
  const allEnums = useGetEnums();
  const [rows, setRows] = useState<NotificationCategoryGridRow[]>([]);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({ count: 1, page: 1, itemPerPage: 50, itemsPerPage: [10, 20, 50, 100] });
  const [selected, setSelected] = useState<NotificationCategory | null>(null);
  const [form, setForm] = useState<NotificationCategory>(emptyCategory);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const load = useCallback(
    async (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effective = pag ?? pagination;
      const effectiveGrid = gridOverride ?? gridItems ?? [];
      const res = await axiosInstance.post(ApiConstants.notifications.categories.listCt, {
        gridItems: effectiveGrid,
        pagination: effective,
      });
      setRows(res.data?.items ?? []);
      setGridItems(normalizeCategoryGridItems(res.data?.gridItems ?? []));
      setPagination((prev) => ({
        ...prev,
        count: res.data?.pagination?.count ?? prev.count,
        page: res.data?.pagination?.page ?? prev.page,
        itemPerPage: res.data?.pagination?.itemPerPage ?? prev.itemPerPage,
        itemsPerPage: res.data?.pagination?.itemsPerPage ?? prev.itemsPerPage,
      }));
    },
    [gridItems, pagination],
  );

  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    if (!canCreate) return;
    setForm(emptyCategory);
    setSelected(null);
    setIsEditOpen(true);
  };

  const openEdit = (row: NotificationCategoryGridRow) => {
    setSelected(row);
    setForm({
      id: row.id,
      type: Number(row.typeId ?? row.type ?? 0),
      name: row.name ?? '',
      defaultSeverity: row.defaultSeverity ?? 'Info',
      isSystem: Boolean(row.isSystem),
      isActive: Boolean(row.isActive),
      createdAt: row.createdAt,
    });
    setIsEditOpen(true);
  };

  const save = async () => {
    if (selected ? !canUpdate : !canCreate) return;
    const payload = {
      id: form.id,
      type: Number(form.type ?? 0),
      name: form.name,
      defaultSeverity: form.defaultSeverity ?? null,
      isSystem: form.isSystem,
      isActive: form.isActive,
      createdAt: form.createdAt,
    };

    await axiosInstance.post(ApiConstants.notifications.categories.save, payload);
    setIsEditOpen(false);
    await load();
  };

  const remove = async () => {
    if (!selected || !canDelete) return;
    await axiosInstance.post(ApiConstants.notifications.categories.delete(selected.id));
    setIsDeleteOpen(false);
    setSelected(null);
    await load();
  };

  const categoryTypes = Array.isArray(allEnums?.NotificationCategoryCode) ? allEnums.NotificationCategoryCode : [];

  const getCategoryTypeLabel = (value?: number | string | null) => {
    const matchById = categoryTypes.find((item: any) => Number(item.id) === Number(value));
    const enumName = matchById?.name ?? String(value ?? '');
    return i18n.t(`enum.notificationCategoryCode.${enumName}`, { defaultValue: enumName });
  };

  return (
    <BasicCli>
      <div className="naWrap bcPageContent">
        <div className="naContainer">
          <div className="bcPageHeader naPageHeader">
            <div className="naHeading">
              <span className="naHeadingIcon"><NotificationsActiveOutlinedIcon /></span>
              <div>
              <h1 className="bcPageTitle">{i18n.t('notifications.categoryTitle', { defaultValue: 'Notification Categories' })}</h1>
              <p className="naSubtitle">{i18n.t('notifications.categorySubtitle', { defaultValue: 'Manage notification category definitions.' })}</p>
              </div>
            </div>
            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn" type="button" onClick={() => void load()}><RefreshRoundedIcon fontSize="small" />{i18n.t('notifications.refresh', { defaultValue: 'Refresh' })}</button>
              <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={openNew} disabled={!canCreate}><AddRoundedIcon fontSize="small" />{i18n.t('generic.createNew', { defaultValue: 'Create new' })}</button>
            </div>
          </div>

          <div className="naTableWrap">
            <GeneralTable
              data={rows}
              columns={normalizeCategoryGridItems(gridItems)}
              gridEntity={19}
              title=""
              hasRp={true}
              selectedItem={(val) => setSelected(val)}
              onEdit={openEdit}
              canEdit={() => canUpdate}
              canDelete={() => canDelete}
              onDelete={(row) => {
                setSelected(row);
                setIsDeleteOpen(true);
              }}
              pagination={pagination}
              selectedItemPagination={(val) => void load(val)}
              onFilterClose={(nextGridItems) => {
                const normalized = normalizeCategoryGridItems(nextGridItems);
                setGridItems(normalized);
                void load(undefined, normalized);
              }}
            />
          </div>
        </div>
      </div>

      {isEditOpen && (
        <EditModal
          title={hasId(form.id)
            ? i18n.t('notifications.editCategory', { defaultValue: 'Edit Category' })
            : i18n.t('notifications.newCategory', { defaultValue: 'New Category' })}
          width="720px"
          closeModal={() => setIsEditOpen(false)}
          setIsModalOpen={setIsEditOpen}
          onSubmitData={() => void save()}
          modalType={hasId(form.id) ? eFormType.Edit : eFormType.New}
        >
          <div className="naForm">
            <label className="naField">
              <span>{i18n.t('notifications.categoryType', { defaultValue: 'Category Type' })}</span>
              <select className="ui-control ui-select" value={form.type} onChange={(e) => setForm((prev) => ({ ...prev, type: Number(e.target.value) }))}>
                <option value={0}>{i18n.t('notifications.selectCategoryType', { defaultValue: 'Select category type' })}</option>
                {categoryTypes.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {getCategoryTypeLabel(item.id)}
                  </option>
                ))}
              </select>
            </label>
            <label className="naField"><span>{i18n.t('generic.name', { defaultValue: 'Name' })}</span><input className="ui-control ui-input" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} /></label>
            <label className="naField">
              <span>{i18n.t('notifications.defaultSeverity', { defaultValue: 'Default Severity' })}</span>
              <select className="ui-control ui-select" value={form.defaultSeverity ?? 'Info'} onChange={(e) => setForm((prev) => ({ ...prev, defaultSeverity: e.target.value }))}>
                <option value="Info">Info</option>
                <option value="Success">Success</option>
                <option value="Warning">Warning</option>
                <option value="Error">Error</option>
              </select>
            </label>
            <div className="naToggleGrid">
              <label className="naCheck"><input className="ui-check" type="checkbox" checked={form.isSystem} onChange={(e) => setForm((prev) => ({ ...prev, isSystem: e.target.checked }))} /> <span><strong>{i18n.t('notifications.system', { defaultValue: 'System' })}</strong><small>Protect this platform category</small></span></label>
              <label className="naCheck"><input className="ui-check" type="checkbox" checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} /> <span><strong>{i18n.t('notifications.active', { defaultValue: 'Active' })}</strong><small>Available for notifications</small></span></label>
            </div>
          </div>
        </EditModal>
      )}

      {isDeleteOpen && selected && (
        <DeleteModal width="460px" handleClose={() => setIsDeleteOpen(false)} handleDelete={() => void remove()}>
          {i18n.t('notifications.deleteCategoryConfirm', {
            defaultValue: 'Delete notification category {{name}}?',
            name: selected.name,
          })}
        </DeleteModal>
      )}
    </BasicCli>
  );
};

export default NotificationCategoriesGrid;
